/**
 * print_and_mark_controller.js — flyer printing + "mark as printed" (Stimulus).
 *
 * Ports static/js/print_and_mark.js to an element-scoped controller. Two jobs:
 *   1. printAndMark() / printAllAndMark(): trigger window.print() then offer to
 *      POST back marking placements as printed. They read the print context from
 *      <body>'s data-* (data-mark-url, data-ids, data-mode, data-copies) and are
 *      invoked via data-action="call-fn" (exposed on window) or onclick.
 *   2. _wirePrintCountBtns(): wire each .print-count-btn dashboard row button to
 *      handlePrintCountClick (inline edit for counts > 1).
 *
 * Lifecycle (replaces the old attachOnce()/turbo:load + DOMContentLoaded guards):
 *   The original wired .print-count-btn via attachOnce()+turbo:load on dashboard
 *   pages (under base layout), falling back to DOMContentLoaded on standalone
 *   print pages. Stimulus unifies both: connect() runs each time the controller's
 *   element is inserted — on Turbo navigation (dashboard) and on hard load
 *   (standalone) alike — so we just re-wire in connect(). The .onclick assignments
 *   replace rather than stack, so re-running is safe.
 *
 * The print/mark functions are stateless and read document.body.dataset (kept
 * exactly), so they stay module-level and remain exposed on window for the
 * template dispatcher (data-action="call-fn") and existing onclick handlers.
 *
 * Usage requires data attributes on <body>:
 *   data-mark-url   — POST endpoint for marking printed
 *   data-ids        — comma-separated placement IDs
 *   data-mode       — 'single' or 'bulk'
 *   data-copies     — number of copies (single mode only)
 */

import { Controller } from "@hotwired/stimulus";

    function printAndMark() {
        window.print();
        var body = document.body;
        var url = body.dataset.markUrl || '';
        var ids = body.dataset.ids || '';
        if (!url || !ids) return;
        if (confirm('Did you print this flyer?\n\nClick OK to mark as printed.')) {
            _submitMarkPrinted(url, ids, 1);
        }
    }

    function printAllAndMark() {
        window.print();
        var body = document.body;
        var url = body.dataset.markUrl || '';
        var ids = body.dataset.ids || '';
        var mode = body.dataset.mode || 'bulk';
        var copies = parseInt(body.dataset.copies || '1', 10);

        if (!url || !ids) return;

        var count = ids.split(',').length;
        var promptMessage = '';

        if (mode === 'single' && copies > 1) {
            promptMessage = 'Did you print ' + copies + ' copies of this flyer?\n\nClick OK to record ' + copies + ' copies as printed.';
        } else {
            promptMessage = 'Did you print ' + count + ' flyer(s)?\n\nClick OK to mark them as printed.';
        }

        if (confirm(promptMessage)) {
            _submitMarkPrinted(url, ids, copies);
        }
    }

    function _submitMarkPrinted(url, ids, copies) {
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        var html = '<input type="hidden" name="ids" value="' + ids + '">' +
                   '<input type="hidden" name="printed" value="true">' +
                   '<input type="hidden" name="copies" value="' + copies + '">';
        // mark-printed routes are CSRF-protected; include the token when the page
        // exposes one (data-csrf-token on <body>), or the POST 400s "CSRF token is missing".
        var csrf = document.body.dataset.csrfToken || '';
        if (csrf) {
            html += '<input type="hidden" name="csrf_token" value="' + csrf + '">';
        }
        form.innerHTML = html;
        document.body.appendChild(form);
        form.submit();
    }

    /**
     * Print count toggle/edit for dashboard placement rows.
     *
     * - print_count == 0: submits printed=true (mark as printed)
     * - print_count == 1: submits printed=false (toggle back to 0)
     * - print_count >  1: swaps button for inline number input; save sets
     *   set_count=N, cancel restores button
     *
     * Called via onclick on .print-count-btn buttons.
     */
    function handlePrintCountClick(btn) {
        var count = parseInt(btn.dataset.printCount || '0', 10);
        var form = btn.closest('.print-count-form');

        if (count <= 1) {
            // Simple toggle — submit the form as-is (printed=true or printed=false)
            form.submit();
            return;
        }

        // count > 1: show inline edit
        var original = btn.outerHTML;

        var input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.max = '9999';
        input.value = count;
        input.style.cssText = 'width:60px;padding:3px 6px;border-radius:4px;border:1px solid #0dcaf0;font-size:13px;text-align:center;';

        var save = document.createElement('button');
        save.type = 'button';
        save.className = 'btn btn-sm btn-info text-white ms-1';
        save.innerHTML = '<i class="bi bi-check-lg"></i>';
        save.title = 'Save';

        var cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'btn btn-sm btn-outline-secondary ms-1';
        cancel.innerHTML = '<i class="bi bi-x-lg"></i>';
        cancel.title = 'Cancel';

        btn.replaceWith(input);
        input.after(save);
        save.after(cancel);
        input.focus();
        input.select();

        cancel.onclick = function() {
            // Restore original button
            var tmp = document.createElement('div');
            tmp.innerHTML = original;
            var restored = tmp.firstChild;
            restored.onclick = function() { handlePrintCountClick(restored); };
            input.replaceWith(restored);
            save.remove();
            cancel.remove();
        };

        save.onclick = function() {
            var newVal = parseInt(input.value, 10);
            if (isNaN(newVal) || newVal < 0) return;
            form.querySelector('[name="set_count"]').value = newVal;
            // Clear printed field so server uses set_count path
            form.querySelector('[name="printed"]').value = '';
            form.submit();
        };

        // Enter key on input triggers save
        input.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') { e.preventDefault(); save.click(); }
            if (e.key === 'Escape') { cancel.click(); }
        });
    }

    // Wire up all .print-count-btn buttons.
    // Idempotent: .onclick assignment replaces rather than stacks, so connect()
    // can re-run it on every navigation safely.
    function _wirePrintCountBtns() {
        document.querySelectorAll('.print-count-btn').forEach(function(btn) {
            btn.onclick = function() { handlePrintCountClick(btn); };
        });
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js)
    // and existing onclick handlers in flyer templates.
    window.printAllAndMark = printAllAndMark;
    window.printAndMark = printAndMark;

export default class extends Controller {
        connect() {
            // Re-wire .print-count-btn rows to the freshly inserted DOM. Runs on
            // both Turbo navigation (dashboard) and hard load (standalone print
            // pages); .onclick replaces rather than stacks, so re-running is safe.
            _wirePrintCountBtns();
        }
}
